import { useThree } from '@react-three/fiber'
import { AuthedAxiosRequests } from 'features/auth'
import {
  DEFAULT_POINT_SIZE,
  PointCloudCache,
  useLoadPotrees,
  usePointCloudContext,
} from 'features/pointClouds'
import { PointCloudOctree, PointShape } from 'gecko-potree-core'
import { useCallback, useMemo } from 'react'
import * as THREE from 'three'
import { GeckoPotreePointCloud } from 'types'

interface PointCloudLoaderProps {
  fileName: string
  potreePointClouds: readonly GeckoPotreePointCloud[]
  entryId: string
  axios: AuthedAxiosRequests
  cache: PointCloudCache
}

const POINT_CLOUD_ROTATION_X = -Math.PI / 2 // Orients the point cloud and maxar to have the same up direction

function initializePointCloud(
  octree: PointCloudOctree,
  pointSize: number
): THREE.Box3 | null {
  const material = octree.material

  if (material) {
    material.size = pointSize
    material.shape = PointShape.CIRCLE
    material.vertexColors = true
  }

  const localBoundingBox = octree.boundingBox as THREE.Box3 | undefined
  if (localBoundingBox) {
    octree.updateMatrixWorld(true)
    const worldBoundingBox = localBoundingBox
      .clone()
      .applyMatrix4(octree.matrixWorld)
    return worldBoundingBox
  } else {
    return null
  }
}

// Store the original camera near value to restore it when clipping is disabled
let originalNear: number | null = null

export function PointCloudLoader({
  fileName,
  axios,
  cache,
  potreePointClouds,
}: PointCloudLoaderProps) {
  const {
    pointSize,
    pointBudget,
    colorMode,
    hueFilterRange,
    gamma,
    enableLegacyRotation,
    setEnableLegacyRotation,
    setPointCloudMinY,
    pointCloudOctreesRef,
    nearClippingDistance,
    enableNearClipping,
  } = usePointCloudContext()

  const { camera } = useThree()
  const { activePointCloud, showPointCloud } = usePointCloudContext()
  const pcSettings = activePointCloud?.data?.default_settings

  const initialSizeForPotree = useMemo(() => {
    return pcSettings?.point_size ?? DEFAULT_POINT_SIZE
  }, [pcSettings])

  const onPointCloudLoad = useCallback(
    (pco: PointCloudOctree, pc: GeckoPotreePointCloud) => {
      pco.raycast = () => false
      if (pc.data.point_cloud_transform) {
        const transform_matrix = pc.data.point_cloud_transform
        pco.applyMatrix4(new THREE.Matrix4(...transform_matrix))
      }
      if (pc.data.use_legacy_rotation) pco.rotateX(POINT_CLOUD_ROTATION_X)
      pco.visible = showPointCloud ?? false

      const existingIndex = pointCloudOctreesRef.current.findIndex(
        (d) => d.uuid === pco.uuid
      )
      if (existingIndex >= 0) {
        pointCloudOctreesRef.current[existingIndex] = pco
      } else {
        pointCloudOctreesRef.current.push(pco)
      }
      const worldBoundingBox = initializePointCloud(pco, initialSizeForPotree)
      setEnableLegacyRotation(pc.data.use_legacy_rotation ?? false)

      if (worldBoundingBox && worldBoundingBox.min) {
        setPointCloudMinY(worldBoundingBox.min.y)
      } else {
        setPointCloudMinY(undefined)
      }
    },
    [
      initialSizeForPotree,
      pointCloudOctreesRef,
      setEnableLegacyRotation,
      setPointCloudMinY,
      showPointCloud,
    ]
  )

  const onApplyInitialSettings = useCallback(
    (pco: PointCloudOctree) => {
      if (pco.material) {
        pco.setRotationFromEuler(new THREE.Euler(0, 0, 0))
        if (enableLegacyRotation) {
          pco.rotateX(POINT_CLOUD_ROTATION_X)
        }
        pco.material.size = pointSize
        pco.material.pointColorType = colorMode
        pco.material.rgbGamma = gamma
        pco.material.uniforms.minHue.value = hueFilterRange[0]
        pco.material.uniforms.maxHue.value = hueFilterRange[1]
        pco.material.vertexColors = true
      }
    },
    [pointSize, colorMode, gamma, hueFilterRange, enableLegacyRotation]
  )

  const onFrameBeforeUpdatePointClouds = useCallback(() => {
    // Apply near clipping by modifying the camera's near plane
    if (enableNearClipping && nearClippingDistance > 0) {
      // Store original near value if  already stored
      if (originalNear === null) {
        originalNear = camera.near
      }

      const newNear = Math.max(nearClippingDistance, 0.01)
      if (camera.near !== newNear) {
        camera.near = newNear
        camera.updateProjectionMatrix()
      }
    } else {
      if (originalNear !== null && camera.near !== originalNear) {
        camera.near = originalNear
        camera.updateProjectionMatrix()
        originalNear = null
      }
    }
  }, [enableNearClipping, nearClippingDistance, camera])

  useLoadPotrees({
    pointCloudOctreesRef,
    pointCloudEntries: potreePointClouds,
    axios,
    cache,
    pointBudget,
    fileName,
    onPointCloudLoad,
    onApplyInitialSettings,
    onFrameBeforeUpdatePointClouds,
  })

  return null
}
